import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {NavContext} from '../../../context/global_context'
import {createSearchParams, useParams, useSearchParams,} from 'react-router-dom'
import {deleteRequest, getRequest, postRequest} from '../../../services/base'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import LinkWithStatus from '../../../antd/common/LinkWithStatus'
import GenerateWiresButton from '../../../antd/common/GenerateWiresButton'
import LoadingSpinnerOverlay from '../../../antd/common/LoadingSpinnerOverlay'
import {Dropdown, Empty, Flex, Form, Modal, Row, Space, Table, Typography} from 'antd'
import InputText from '../../../antd/common/InputText'
import Button from '../../../antd/common/Button'
import EllipsisTooltip from '../../../antd/common/EllipsisTooltip'
import {DeleteOutlined, ExclamationCircleOutlined, MoreOutlined} from '@ant-design/icons'
import useDebouncedValue from '../../../utils/common_function'
import UploadSvgImage from './UploadSvgImage'
import RecordsCount from '../../../antd/common/RecordsCount'
import { notifyError } from '../../../utils/notifications'

const PowerDistributions = (props) => {
  const intl = useIntl()
  let { vehicle } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [powerDistData, setPowerDistData] = useState([])
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    current_page: 1,
  })
  const [generateWireButtonStatus, setGenerateWireButtonStatus] =
    useState(false)

  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') ? searchParams.get('search') : ''
  )
  const debouncedSearch = useDebouncedValue(searchQuery, 500)

  const [form] = Form.useForm()
  const [modal, contextHolder] = Modal.useModal()
  const { currentPage, totalCount, totalPages, nextPageLink, prevPageLink } =
    pagination

  const [value, dispatch] = useContext(NavContext)
  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`
  const currentUrl = useRef(
    `/api/power_distributions/?search=${searchQuery}&vehicle=${vehicle}`
  )

  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: `${intl.formatMessage({ id: 'layout.vehicleList' })} / ${vehicle} / ${intl.formatMessage({ id: 'powerDistributions.title' })}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        vehicle: '',
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getPowerDistributions(null)
    getGenerateWireButtonStatus()
  }, [])

  useEffect(() => {
    currentUrl.current = `/api/power_distributions/?search=${debouncedSearch}&vehicle=${vehicle}`
    getPowerDistributions()
  }, [debouncedSearch])

  const getPowerDistributions = async (url = null) => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'powerDistributions.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })
    let response = await getRequest(currentUrl.current)
    setPowerDistData(response.results)
    setPagination(response.pagination)
    dispatch({
      type: 'loader',
      data: {},
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (response?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'powerDistributions.loadError' }))
    }
  }

  const deletePowerDistribution = async (id) => {
    // console.log(`Delete | Id: ${id}`)
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'powerDistributions.deleting' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await deleteRequest(
      `/api/power_distributions/delete/${vehicle}/${id}/`
    )
    dispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'powerDistributions.deleteSuccess' }),
      },
    })
    getPowerDistributions()
  }

  const confirmDeletePowerDistribution = (id) => {
    const title = intl.formatMessage({ id: 'powerDistributions.deleteTitle' })
    const content = intl.formatMessage({ id: 'powerDistributions.deleteConfirm' })

    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk: () => {
        deletePowerDistribution(id)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const onGenerateWiresClick = async (connectors = []) => {
    // console.log(connectors)
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'powerDistributions.processingWires' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await postRequest(`/api/wire/generate_wires/`, {
      vehicle: vehicle,
      connectors: connectors,
      clean_wires: false,
      is_inspection_points_wire: false,
      is_power_distribution_wire: true,
    })
    dispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'powerDistributions.wireGenerationStarted' }),
      },
    })
    getGenerateWireButtonStatus()
  }

  const getGenerateWireButtonStatus = async () => {
    let response = await getRequest(
      `/api/dtcs/generate_wire_button_status/?vehicle=${vehicle}`
    )
    setGenerateWireButtonStatus(response.show_loader || false)
    if (response.show_loader) {
      setTimeout(() => {
        getGenerateWireButtonStatus()
      }, 7000)
    }
  }

  const downloadPackage = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'powerDistributions.downloadingPackage' }),
        showLoader: true,
      },
      notification: {},
    })

    let formData = new FormData()
    let res = await postRequest(
      `/api/power_distributions/download_package/${vehicle}/`,
      formData,
      true
    )
    // Was `if (res?.msg)`: a failure carrying no message fell through and saved
    // the error object as the downloaded package.
    if (res?.status === false || !(res instanceof Blob)) {
      dispatch({
        type: 'notification',
        data: { notification_type: 'error', msg: intl.formatMessage({ id: 'powerDistributions.downloadError' }) },
      })
    } else {
      const url = window.URL.createObjectURL(new Blob([res]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `power_distribution.zip`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'powerDistributions.downloadSuccess' }),
          loader: true,
        },
      })
    }
  }

  const dataSource = useMemo(() => {
    if (!powerDistData?.length) return []

    return powerDistData.map((power_distribution, index) => {
      return {
        ...power_distribution,
        key: `${power_distribution.name}_${power_distribution.connector}-${index}`,
      }
    })
  }, [powerDistData])

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'sidebar.powerDistribution' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">{text}</EllipsisTooltip>
        ),
      },
      {
        title: '',
        align: 'right',
        width: 400,
        render: (_, record) => {
          return (
            <Flex
              justify="flex-end"
              align="center"
              gap={16}
              className="full-width"
            >
              <UploadSvgImage
                id={record.id}
                className="svg-col"
                svgImageFilename={record.svg}
                svgDisplayName={record.svg_image_filename}
                getPowerDistributions={() => getPowerDistributions(null)}
              />

              {record.wires_generated ? (
                <Typography.Text>{intl.formatMessage({ id: 'powerDistributions.wiresGenerated' })}</Typography.Text>
              ) : (
                <Button
                  type="link"
                  className="link-button"
                  style={{ width: 116 }}
                  onClick={() => onGenerateWiresClick([record.connector])}
                >
                  {intl.formatMessage({ id: 'powerDistributions.generateWires' })}
                </Button>
              )}

              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'delete',
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => confirmDeletePowerDistribution(record.id),
                      label: intl.formatMessage({
                        id: 'powerDistributions.delete',
                      }),
                    },
                  ],
                }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </Flex>
          )
        },
      },
    ],
    [dataSource]
  )

  return (
    <>
      {generateWireButtonStatus && (
        <div>
          <LoadingSpinnerOverlay
            active={generateWireButtonStatus}
            text={intl.formatMessage({ id: 'powerDistributions.wireGenerationInProgress' })}
          />
        </div>
      )}

      <div className="page power-distribution">
        <Row className="title">
          <Space size={'middle'}>
            <Typography.Title level={5}>{intl.formatMessage({ id: 'sidebar.powerDistribution' })}</Typography.Title>
          </Space>
        </Row>

        <Row className="body">
          <Form form={form} className="power-distribution-form full-width">
            <Flex justify="space-between" className="full-width data-filters" gap={16}>
              <InputText
                name="search"
                type="search"
                placeholder={intl.formatMessage({ id: 'powerDistributions.searchPlaceholder' })}
                value={searchQuery}
                isRequired={false}
                wrapperClass="search-connector"
                onChange={(value) => {
                  setSearchQuery(value)
                }}
              />

              <Space
                direction="horizontal"
                size="middle"
                className="ant-checkbox-group ant-form-item-inline"
              >
                <LinkWithStatus
                  href={`/vehicles/new`}
                  onClickFunc={() => {
                    downloadPackage()
                  }}
                >
                  {intl.formatMessage({ id: 'powerDistributions.downloadPackage' })}
                </LinkWithStatus>

                <GenerateWiresButton
                  is_power_distribution_wire={true}
                  onSuccess={() => getPowerDistributions()}
                />
              </Space>
            </Flex>
          </Form>
        </Row>

        <Row>
          <Table
            rowKey="key"
            columns={columns}
            dataSource={dataSource}
            showSorterTooltip={false}
            tableLayout="fixed"
            pagination={
              pagination
                ? {
                    current: currentPage,
                    total: totalCount,
                    pageSize: 10,
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                    showTotal: (total, range) => (
                      <RecordsCount count={total} />
                    ),
                    onChange: (page) => {
                      // 10 is page size
                      const offset = (page - 1) * 10
                      const url = nextPageLink
                        ? new URL(nextPageLink)
                        : new URL(prevPageLink)
                      url.searchParams.set('offset', offset)
                      currentUrl.current = url.toString()
                      getPowerDistributions()
                    },
                  }
                : false
            }
            locale={{
              emptyText: (
                <div style={{ padding: '40px' }}>
                  <Empty 
                    description={
                      <RecordsCount count={0} />
                    } 
                  />
                </div>
              ),
            }}
          />
        </Row>
        {totalPages <= 1 && totalCount > 0 && (
          <Typography.Text>
            <RecordsCount count={totalCount} />
          </Typography.Text>
        )}
      </div>

      {contextHolder}
    </>
  )
}

export default PowerDistributions
